import type { PointTransaction } from "@/models/point-transaction";
import { apiGet, apiPost, buildQueryString, type QueryValue } from "../client";
import type { Datasource } from "../types";

export interface TransactionCalculation {
  purchase_value_cents: number;
  purchase_value_formatted: string;
  raw_points_for_purchase: number;
  raw_points_for_rewards: number;
  points_to_spend: number;
  points_to_earn: number;
  covered_value_cents: number;
  covered_value_formatted: string;
  cash_value_cents: number;
  cash_value_formatted: string;
  has_sufficient_balance: boolean;
  multiplier: number;
  points: number;
}

export interface TransactionCalculationResponse {
  calculation: TransactionCalculation;
}

export interface CreateTransactionParams {
  loyalty_account_id: number;
  purchase_value_cents: number;
  points?: number;
  transaction_type: string;
  station_id: string | null;
  description?: string;
  reversed_transaction_id?: string;
  idempotencyKey: string;
}

export interface CreateTransactionResponse {
  transaction?: PointTransaction;
}

export interface CalculateParams {
  loyalty_account_id: number;
  purchase_value_cents: number;
  transaction_type?: string;
  points?: number;
}

export async function getTransaction(id: string): Promise<PointTransaction> {
  const response = await apiGet<{ transaction: PointTransaction }>(`/point_transactions/${id}`);
  return response.transaction;
}

export async function calculateTransaction(
  params: CalculateParams,
): Promise<Datasource.Response<TransactionCalculationResponse>> {
  const queryString = buildQueryString({
    loyalty_account_id: params.loyalty_account_id,
    purchase_value_cents: params.purchase_value_cents,
    transaction_type: params.transaction_type,
    points: params.points,
  });

  const response = await apiGet<Datasource.Response<TransactionCalculationResponse>>(
    `/point_transactions/calculate?${queryString}`,
  );

  return response;
}

export async function createTransaction(
  params: CreateTransactionParams,
): Promise<Datasource.Response<CreateTransactionResponse>> {
  const { idempotencyKey, ...body } = params;
  return apiPost<Datasource.Response<CreateTransactionResponse>>("/point_transactions", body, {
    headers: {
      "Idempotency-Key": idempotencyKey,
    },
  });
}

export interface SearchTransactionsParams {
  search?: string;
  type?: string;
  date_range?: string;
  station_id?: string;
  loyalty_account_id?: number;
  loyalty_plan_id?: number;
  page?: number;
  per_page?: number;
}

export interface SearchTransactionsResponse {
  transactions: PointTransaction[];
}

export async function searchTransactions(
  params: SearchTransactionsParams,
): Promise<Datasource.Response<SearchTransactionsResponse>> {
  const queryString = buildQueryString(params as Record<string, QueryValue>);

  return apiGet<Datasource.Response<SearchTransactionsResponse>>(
    `/point_transactions?${queryString}`,
  );
}
